import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, HeartHandshake } from "lucide-react";

import CopyText from "../CopyText/CopyText";
import { Modal } from "../Modal/Modal";
import ExternalLinkButton from "../ExternalLinkButton/ExternalLinkButton";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import { useReferral } from "../../hooks/useReferral";

type ReferralProgramContentProps = {
  isOpen: boolean;
  subtitleOverride?: string;
  benefitsOverride?: string[];
};

export const ReferralProgramContent = ({
  isOpen,
  subtitleOverride,
  benefitsOverride,
}: ReferralProgramContentProps) => {
  const { t } = useTranslation();

  const {
    referralLink,
    referralLanding,
    referredStoresCount,
    isLoadingReferralData,
    handleCopy,
  } = useReferral(isOpen);

  const subtitle = subtitleOverride ?? t("referrals.subtitle");

  const benefits = benefitsOverride ?? [
    t("referrals.brand.reward"),
    t("referrals.store.reward"),
    t("referrals.qualifying"),
    t("referrals.payment"),
  ];

  const linkLabel = t("referrals.link_label");
  const readMoreLabel = t("referrals.read_more");
  const copyButtonLabel = t("referrals.copy_button");
  const counterLabel = t("referrals.counter_label");

  const renderReferralLink = () => {
    if (isLoadingReferralData) {
      return (
        <div className="flex flex-wrap items-center gap-2 bg-gray-100 p-2 rounded-md overflow-hidden">
          <div className="h-5 bg-gray-300 rounded animate-pulse flex-1 min-w-[200px]" />
          <div className="h-5 w-20 bg-gray-300 rounded animate-pulse" />
          <div className="h-8 w-8 bg-gray-300 rounded animate-pulse shrink-0" />
        </div>
      );
    }

    const url = new URL(referralLink);
    const basePart = `${url.protocol}//${url.host}${url.pathname}`;
    const queryPart = url.search;

    return (
      <div className="flex flex-wrap items-center gap-2 bg-gray-100 p-2 rounded-md overflow-hidden">
        <span className="text-gray-500 truncate w-full sm:w-auto">
          {basePart}
        </span>
        <span className="font-medium truncate w-full sm:w-auto">
          {queryPart}
        </span>
        <div className="shrink-0">
          <CopyText text={referralLink} actionName="Copy Referral Link" />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 text-left">
      <div className="flex items-center justify-center gap-6">
        <span className="inline-flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-rose-400 via-pink-500 to-fuchsia-500 text-white shadow-lg">
          <HeartHandshake className="h-12 w-12 animate-[heartbeat_1.5s_ease-in-out_infinite]" />
        </span>
        <style>{`
                @keyframes heartbeat {
                  0%, 100% { transform: scale(1); }
                  10%, 30% { transform: scale(1.1); }
                  20%, 40% { transform: scale(1); }
                  50% { transform: scale(1.15); }
                }
              `}</style>
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-1">
            {counterLabel}
          </p>
          {isLoadingReferralData ? (
            <div className="h-16 w-20 mx-auto bg-gradient-to-r from-rose-200 via-pink-200 to-fuchsia-200 rounded-lg animate-pulse" />
          ) : (
            <p className="text-5xl font-bold bg-gradient-to-r from-rose-500 via-pink-600 to-fuchsia-600 bg-clip-text text-transparent">
              {referredStoresCount}
            </p>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-700">{subtitle}</p>

      <div className="space-y-3">
        {benefits.map((benefit, index) => (
          <div key={`${benefit}-${index}`} className="flex items-start text-gray-700">
            <Check className="mr-2 h-5 w-5 flex-shrink-0 text-green-500" />
            <p className="text-sm">{benefit}</p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {linkLabel}
        </p>
        {renderReferralLink()}
        <div className="flex justify-center">
          <PrimaryButton
            text={copyButtonLabel}
            fn={handleCopy}
            actionName="Copy Referral Link"
          />
        </div>
      </div>

      <ExternalLinkButton
        text={readMoreLabel}
        href={referralLanding}
        variant="default"
        className="text-sm"
        iconSize="sm"
      />
    </div>
  );
};

const ReferralProgramDialog = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  const modalTitle = t("referrals.title");
  const triggerLabel = t("referrals.trigger");

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        aria-label={triggerLabel}
        title={triggerLabel}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-2.5 py-2 text-xs font-semibold text-white shadow-lg transition hover:scale-105 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-400 lg:px-4"
      >
        <HeartHandshake className="h-4 w-4 shrink-0" aria-hidden />
        <span className="hidden lg:inline">{triggerLabel}</span>
      </button>

      {isOpen && (
        <Modal modalTitle={modalTitle} canClose onClose={closeModal}>
          <ReferralProgramContent isOpen={isOpen} />
        </Modal>
      )}
    </>
  );
};

export default ReferralProgramDialog;

